// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Collapse } from "react-bootstrap";
import { Link } from "react-router-dom";

import { MainPanel } from "types";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { isSet } from "shared/utils/utils";
import { IAction, ActionType, raiseAction } from "actions";
import { IState } from "states/states";
import { IRfid } from "states/ocppstates";
import Rfid from "model/rfid";

import ModalRfidSettings from "view/modal/ModalRfidSettings";
import RfidList from "../comp/RfidList";
import RfidStatistics from "view/comp/RfidStatistics";
import RfidData from "view/comp/RfidData";
import ChargingSessionList from "./ChargingSessionList";
import SiteBar from "view/comp/SiteBar";

import IconExpandMore from "icons/iconExpandMore";
import IconExpandLess from "icons/iconExpandLess";
import IconPrint from "icons/iconPrint";

interface IRfidPageProps {
  siteId: number;
  isRfidDetail: boolean;
  rfids: IRfid[];
  selectedRfid: number | undefined;
  rfidFilter: string | undefined;
  canModifyRfids: boolean;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);

  const props: IRfidPageProps = {
    siteId: state.cached.selectedSite!,
    isRfidDetail: state.router.panel === MainPanel.RFIDDETAIL,
    rfids: state.ocpp.rfid,
    selectedRfid: state.router.rfid,
    rfidFilter: state.router.rfidFilter,
    canModifyRfids:
      (curUser?.accessRights.ocppSettings ?? false) &&
      isSet(state.scheduler.ocppRfidListExt.lastData),
  };
  return props;
};

interface IRfidPageState {
  isExpanded: boolean;
}

interface IRfidPageDispatch {
  onEdit: (id: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRfidPageDispatch = {
    onEdit: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_RFID, id));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  connector: {
    id: "Connector",
  },
  editHint: {
    id: "change settings",
  },
  printHint: {
    id: "print",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  filteredCards: { filter: string; count: number };
}>({
  filteredCards: {
    id: "Filtered charging cards: {filter} ({count})",
  },
});

class RfidPageComponent extends React.Component<
  IRfidPageProps & IRfidPageDispatch & RoutedProps,
  IRfidPageState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public state: IRfidPageState = {
    isExpanded: false,
  };

  public render() {
    return (
      <>
        <SiteBar showPeriodSelect={true} />
        <div id="rfidpage" className="row">
          {this.props.isRfidDetail && (
            <div className="flex-content d-print-none">
              <Button id="navigate-back" className="d-md-none" variant="secondary">
                <Link to={`../${MainPanel.RFIDLIST}`}>
                  <FormattedMessage id="Back to Rfid List" />
                </Link>
              </Button>
            </div>
          )}
          {this.renderLeft()}
          {this.renderRight()}
        </div>
        <ModalRfidSettings />
      </>
    );
  }

  private renderLeft() {
    return (
      <div
        id="rfidpage-left"
        className={`d-print-none ${
          this.props.isRfidDetail ? "d-none d-md-block" : "col-12"
        } col-md-5 col-lg-4 col-xl-4`}
      >
        <h1>
          <FormattedMessage id="Charging Cards" />
        </h1>
        {this.props.rfids.length > 3 && <RfidStatistics />}
        <RfidList isDashboard={false} />
      </div>
    );
  }

  private renderRight() {
    const selectedRfid = this.props.rfids.find((curItem) => curItem.id === this.props.selectedRfid);

    return (
      <div
        id="rfidpage-right"
        className={`${
          this.props.isRfidDetail ? "col-12" : "d-none d-md-block d-print-block"
        } col-md-7 col-lg-8 col-xl-8`}
      >
        {isSet(selectedRfid) && (
          <div id="rfidinfo">
            <div id="rfidinfo-heading">
              <h1
                className="link"
                onClick={() => {
                  this.setState({ isExpanded: !this.state.isExpanded });
                }}
              >
                <Button variant="link" className="expand-button me-2 d-print-none">
                  {this.state.isExpanded ?
                    <IconExpandMore />
                  : <IconExpandLess />}
                </Button>
                {selectedRfid?.name ? selectedRfid?.name : selectedRfid?.rfid}
              </h1>
              <div className="buttonbar">
                {this.props.canModifyRfids &&
                  this.state.isExpanded &&
                  selectedRfid?.siteId === this.props.siteId && (
                    <Button
                      id="editRfid"
                      className="nowrap d-print-none"
                      variant="secondary"
                      title={this.context.formatMessage(messages.editHint)}
                      onClick={(_evt) => this.props.onEdit(selectedRfid.id)}
                    >
                      <FormattedMessage id="Edit Charging Card" />
                    </Button>
                  )}
                <Button
                  className="ms-1 d-print-none"
                  variant="secondary"
                  title={this.context.formatMessage(messages.printHint)}
                  onClick={() => {
                    window.print();
                  }}
                >
                  <IconPrint />
                </Button>
              </div>
            </div>
            <Collapse in={this.state.isExpanded}>
              <div className="row">
                <div className="mb-4">
                  <RfidData />
                </div>
              </div>
            </Collapse>
          </div>
        )}
        {!isSet(selectedRfid) && isSet(this.props.rfidFilter) && (
          <div id="rfidinfo">
            <div id="rfidinfo-heading">
              <h1>
                {this.context.formatMessage(valueMessages.filteredCards, {
                  filter: this.props.rfidFilter,
                  count: this.props.rfids.filter(
                    (curRfid) =>
                      curRfid.isActive && new Rfid(curRfid).matchesFilter(this.props.rfidFilter!),
                  ).length,
                })}
              </h1>
            </div>
          </div>
        )}
        <ChargingSessionList isElementSelected={isSet(selectedRfid)} />
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(RfidPageComponent));
